// src/services/solana.service.ts (UPDATED WITH MOBILE TRANSACTION SUPPORT)
import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
  Keypair,
} from '@solana/web3.js';
import {
  createInitializeMintInstruction,
  createInitializeAccountInstruction,
  createMintToInstruction,
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  MINT_SIZE,
  createSetAuthorityInstruction,
  AuthorityType,
} from '@solana/spl-token';
import { TokenFormData } from '../types';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import phantomDeeplinkService from './phantom-deeplink.service';

interface PhantomProvider {
  isPhantom?: boolean;
  publicKey?: PublicKey;
  isConnected?: boolean;
  connect: () => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  signTransaction: (transaction: Transaction) => Promise<Transaction>;
  signAllTransactions: (transactions: Transaction[]) => Promise<Transaction[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
}

class SolanaService {
  private connection: Connection;
  private provider: PhantomProvider | null = null;

  constructor() {
    this.connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
  }

  // Check if running on mobile
  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  // Check if we're in Phantom's mobile browser
  private isPhantomMobileBrowser(): boolean {
    return !!(window as any).solana?.isPhantom && this.isMobile();
  }

  private getProvider(): PhantomProvider {
    if ('solana' in window) {
      const provider = (window as any).solana;
      if (provider?.isPhantom) {
        return provider as PhantomProvider;
      }
    }
    throw new Error('Phantom wallet not found');
  }

  async connect(): Promise<string> {
    try {
      // Check if we have a stored session from deeplink connection
      if (phantomDeeplinkService.hasValidSession()) {
        const storedPublicKey = phantomDeeplinkService.getStoredPublicKey();
        if (storedPublicKey) {
          console.log('Using stored Phantom connection:', storedPublicKey);
          return storedPublicKey;
        }
      }

      // Mobile handling - use deeplink service
      if (this.isMobile() && !this.isPhantomMobileBrowser()) {
        // Clear any timed out attempts
        if (phantomDeeplinkService.isConnectionTimedOut()) {
          phantomDeeplinkService.clearSession();
        }
        
        return await phantomDeeplinkService.connect();
      }

      // Desktop or Phantom browser - direct connection
      this.provider = this.getProvider();

      // Already connected?
      if (this.provider.isConnected && this.provider.publicKey) {
        return this.provider.publicKey.toString();
      }

      const resp = await this.provider.connect();
      console.log('Connected to Phantom wallet:', resp.publicKey.toString());
      return resp.publicKey.toString();
    } catch (error) {
      console.error('Failed to connect to Phantom:', error);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    try {
      // Clear deeplink session
      phantomDeeplinkService.clearSession();
      
      // Disconnect provider if available
      if (this.provider) {
        await this.provider.disconnect();
        this.provider = null;
      }
    } catch (error) {
      console.error('Failed to disconnect from Phantom:', error);
      throw error;
    }
  }

  async getBalance(address?: string): Promise<number> {
    try {
      let pubkey: PublicKey | undefined;

      if (address) {
        pubkey = new PublicKey(address);
      } else {
        // Try provider first, then stored key from deeplink
        if (this.provider?.publicKey) {
          pubkey = this.provider.publicKey;
        } else {
          const storedKey = phantomDeeplinkService.getStoredPublicKey();
          if (storedKey) {
            pubkey = new PublicKey(storedKey);
          }
        }
        
        if (!pubkey) {
          throw new Error('Wallet not connected');
        }
      }

      const balanceLamports = await this.connection.getBalance(pubkey, 'confirmed');
      return balanceLamports / LAMPORTS_PER_SOL;
    } catch (error) {
      console.error('Failed to get balance:', error);
      throw error;
    }
  }

async signTransaction(transaction: Transaction): Promise<string> {
  try {
    // Check if we're on mobile and not in Phantom browser
    const isMobile = this.isMobile();
    const isInPhantomBrowser = this.isPhantomMobileBrowser();
    
    if (isMobile && !isInPhantomBrowser) {
      // Mobile: Use deeplink service
      console.log('Using mobile deeplink transaction signing...');
      
      // The deeplink service will handle the redirect
      // This will throw with "Redirected" message, which is expected
      return await phantomDeeplinkService.signTransaction(transaction);
    } else if (this.provider) {
      // Desktop/Phantom browser: Direct signing
      const signedTransaction = await this.provider.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      await this.connection.confirmTransaction(signature);
      return signature;
    } else {
      // Fallback: Try to get provider
      this.provider = this.getProvider();
      if (this.provider) {
        const signedTransaction = await this.provider.signTransaction(transaction);
        const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
        await this.connection.confirmTransaction(signature);
        return signature;
      } else {
        throw new Error('No wallet provider available');
      }
    }
  } catch (error: any) {
    // Handle the expected "Redirected" error for mobile
    if (error.message === 'Redirected' || error.message?.includes('Redirecting')) {
      // This is expected for mobile deeplink flow
      console.log('Redirecting to Phantom for signing...');
      throw error; // Re-throw to let the calling code know
    }
    
    console.error('Failed to sign transaction:', error);
    throw error;
  }
}

// Remove or update the signAllTransactions method since we don't need it for mobile:
async signAllTransactions(transactions: Transaction[]): Promise<Transaction[]> {
  try {
    // Check if we're on mobile
    const isMobile = this.isMobile();
    const isInPhantomBrowser = this.isPhantomMobileBrowser();
    
    if (isMobile && !isInPhantomBrowser) {
      // Mobile doesn't support batch signing
      // The calling code should use single transaction method instead
      throw new Error('Batch signing not supported on mobile. Use single transaction with multiple transfers instead.');
    }
    
    // Desktop/Phantom browser
    if (this.provider && this.provider.signAllTransactions) {
      return await this.provider.signAllTransactions(transactions);
    } else if (this.provider && this.provider.signTransaction) {
      // Fallback to sequential signing
      const signedTransactions: Transaction[] = [];
      for (const tx of transactions) {
        const signed = await this.provider.signTransaction(tx);
        signedTransactions.push(signed);
      }
      return signedTransactions;
    } else {
      throw new Error('Wallet does not support batch signing');
    }
  } catch (error) {
    console.error('Failed to sign transactions:', error);
    throw error;
  }
}

async signAndSendTransaction(transaction: Transaction): Promise<string> {
  try {
    const isMobile = this.isMobile();
    const isInPhantomBrowser = this.isPhantomMobileBrowser();

    if (isMobile && !isInPhantomBrowser) {
      // Mobile outside Phantom → use deeplink (returns "Redirected" throw)
      console.log('Using mobile deeplink signAndSend...');
      return await phantomDeeplinkService.signAndSendTransaction(transaction);
    }

    // Desktop / Phantom in-app: sign via provider and send
    if (!this.provider) {
      this.provider = this.getProvider();
    }

    const signedTx = await this.provider.signTransaction(transaction);
    const sig = await this.connection.sendRawTransaction(signedTx.serialize(), {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
    });
    await this.connection.confirmTransaction(sig, 'confirmed');
    return sig;
  } catch (error: any) {
    if (error?.message === 'Redirected' || error?.message?.includes('Redirecting')) {
      // expected on mobile deeplink
      throw error;
    }
    console.error('signAndSendTransaction failed:', error);
    throw error;
  }
}

async handleSigningRedirect(): Promise<any> {
  // Delegate to the deeplink service so it decrypts and normalizes Phantom’s response
  return phantomDeeplinkService.handleSigningRedirect();
}


  async signMessage(message: string): Promise<string> {
    try {
      if (this.provider) {
        const encodedMessage = new TextEncoder().encode(message);
        const { signature } = await this.provider.signMessage(encodedMessage);
        return Buffer.from(signature).toString('base64');
      } else {
        // 🚀 NEW: Mobile message signing
        console.log('Using mobile deeplink message signing...');
        return await phantomDeeplinkService.signMessage(message);
      }
    } catch (error) {
      console.error('Failed to sign message:', error);
      throw error;
    }
  }

  async createToken(data: TokenFormData): Promise<string> {
    try {
      let userPublicKey: PublicKey;

      // Get user's public key from provider or stored key
      if (this.provider?.publicKey) {
        userPublicKey = this.provider.publicKey;
      } else {
        const storedKey = phantomDeeplinkService.getStoredPublicKey();
        if (storedKey) {
          userPublicKey = new PublicKey(storedKey);
        } else {
          throw new Error('Wallet not connected');
        }
      }

      // Generate a new keypair for the mint
      const mintKeypair = Keypair.generate();
      const mintPublicKey = mintKeypair.publicKey;

      // Get the minimum balance for rent exemption
      const mintRent = await this.connection.getMinimumBalanceForRentExemption(MINT_SIZE);

      // Create transaction
      const transaction = new Transaction();

      // Create mint account
      transaction.add(
        SystemProgram.createAccount({
          fromPubkey: userPublicKey,
          newAccountPubkey: mintPublicKey,
          space: MINT_SIZE,
          lamports: mintRent,
          programId: TOKEN_PROGRAM_ID,
        })
      );

      // Initialize mint
      const decimals = 9; // Standard for Solana tokens
      transaction.add(
        createInitializeMintInstruction(
          mintPublicKey,
          decimals,
          userPublicKey, // Mint authority
          userPublicKey // Freeze authority (can be null)
        )
      );

      // Get associated token account for the user
      const userTokenAccount = await getAssociatedTokenAddress(mintPublicKey, userPublicKey);

      // Create associated token account
      transaction.add(
        createAssociatedTokenAccountInstruction(
          userPublicKey,
          userTokenAccount,
          userPublicKey,
          mintPublicKey
        )
      );

      // Mint tokens to user's account
      const amount = BigInt(data.supply) * BigInt(10 ** decimals);
      transaction.add(
        createMintToInstruction(mintPublicKey, userTokenAccount, userPublicKey, amount, [])
      );

      // Optionally revoke mint authority (prevents more tokens from being created)
      if (data.lpLock === 100) {
        transaction.add(
          createSetAuthorityInstruction(
            mintPublicKey,
            userPublicKey,
            AuthorityType.MintTokens,
            null
          )
        );
      }

      // Get recent blockhash
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userPublicKey;

      // Sign transaction with both mint keypair and user wallet
      transaction.partialSign(mintKeypair);

      // Request signature from Phantom
      const signedTransaction = await this.signTransaction(transaction);

      // Wait for confirmation
      await this.connection.confirmTransaction({
        signature: signedTransaction,
        blockhash,
        lastValidBlockHeight,
      });

      console.log('Token created successfully!');
      console.log('Token address:', mintPublicKey.toString());
      console.log('Transaction signature:', signedTransaction);

      // Store token metadata (temporary local storage)
      this.storeTokenMetadata(mintPublicKey.toString(), data);

      return mintPublicKey.toString();
    } catch (error) {
      console.error('Failed to create token:', error);
      throw error;
    }
  }

  private storeTokenMetadata(tokenAddress: string, data: TokenFormData) {
    const metadata = {
      address: tokenAddress,
      name: data.name,
      symbol: data.symbol,
      description: data.description,
      image: data.imageUrl,
      supply: data.supply,
      decimals: 9,
      createdAt: new Date().toISOString(),
    };

    const existingTokens = JSON.parse(localStorage.getItem('created_tokens') || '[]');
    existingTokens.push(metadata);
    localStorage.setItem('created_tokens', JSON.stringify(existingTokens));
  }

  // Helper method to check if Phantom is installed
  isPhantomInstalled(): boolean {
    if (this.isMobile()) {
      // On mobile, we can always attempt deeplink connection
      return true;
    }
    return typeof window !== 'undefined' && !!(window as any).solana?.isPhantom;
  }

  // Helper method to get the current connected address
  getCurrentAddress(): string | null {
    return this.provider?.publicKey?.toString() || phantomDeeplinkService.getStoredPublicKey();
  }

  // Helper method to request an airdrop (devnet/testnet only)
  async requestAirdrop(amount: number = 1): Promise<string> {
    try {
      let publicKey: PublicKey;
      
      if (this.provider?.publicKey) {
        publicKey = this.provider.publicKey;
      } else {
        const storedKey = phantomDeeplinkService.getStoredPublicKey();
        if (storedKey) {
          publicKey = new PublicKey(storedKey);
        } else {
          throw new Error('Wallet not connected');
        }
      }

      if (SOLANA_RPC_ENDPOINT.includes('devnet') || SOLANA_RPC_ENDPOINT.includes('testnet')) {
        const signature = await this.connection.requestAirdrop(
          publicKey,
          amount * LAMPORTS_PER_SOL
        );

        await this.connection.confirmTransaction(signature);
        console.log('Airdrop successful:', signature);
        return signature;
      } else {
        throw new Error('Airdrop only available on devnet/testnet');
      }
    } catch (error) {
      console.error('Airdrop failed:', error);
      throw error;
    }
  }
}

export default new SolanaService();